import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { setCloudSSO, setDesktopSSO } from '@/reducers/auth/sso.redux'
import { closeModal } from '@/reducers/ui/ui.redux'

export const createOrUpdateCloudSSOConfig =
  ({ metadata_url, cloud_sso_entries, previous_entries, organization_id }) =>
  async (dispatch) => {
    try {
      const existingIds = new Set(previous_entries.map((entry) => entry.id).filter(Boolean))
      const currentIds = new Set(cloud_sso_entries.map((entry) => entry.id).filter(Boolean))

      const deletedIds = [...existingIds].filter((id) => !currentIds.has(id))

      for (const id of deletedIds) {
        try {
          await fetchingAPI(`${apiService.auth}/sso/config/cloud/${id}`, 'DELETE', dispatch)
        } catch (err) {
          console.warn(`Failed to delete cloud SSO config with id=${id}:`, err)
        }
      }

      const updatedConfigs = []
      for (const entry of cloud_sso_entries) {
        const method = entry.id ? 'PATCH' : 'POST'
        const url = entry.id
          ? `${apiService.auth}/sso/config/cloud/${entry.id}`
          : `${apiService.auth}/sso/config/cloud`

        const body = {
          ...entry,
          organization_id, // inject org_id into each payload
        }

        const savedConfig = await fetchingAPI(url, method, dispatch, JSON.stringify(body))
        updatedConfigs.push(savedConfig)
      }

      if (updatedConfigs.length === 0) {
        dispatch(setCloudSSO({ metadata_url: '', cloud_sso_entries: [] }))
        if (deletedIds.length > 0) {
          toast.success('All cloud SSO entries deleted and metadata URL cleared')
        }
      } else {
        dispatch(setCloudSSO({ metadata_url, cloud_sso_entries: updatedConfigs }))
        toast.success('Cloud SSO config updated successfully')
      }

      dispatch(closeModal())
    } catch (err) {
      toast.error('Failed to update cloud SSO config', { cause: err })
      console.error('Failed to update cloud SSO config entries:', err)
      dispatch(closeModal())
    }
  }

export const fetchSSOConfig = (orgId) => async (dispatch) => {
  try {
    const configs = await fetchingAPI(
      `${apiService.auth}/sso/config/cloud/org/${orgId}`,
      'GET',
      dispatch
    )

    if (!configs || configs.length === 0) {
      dispatch(setCloudSSO(null))
      return
    }

    // TODO this logic may need to change if ever we have diff metadata urls per domain
    const metadata_url = configs[0]?.metadata_url || ''
    const cloud_sso_entries = configs.map(({ metadata_url, ...rest }) => rest)

    dispatch(
      setCloudSSO({
        metadata_url,
        cloud_sso_entries,
      })
    )
  } catch (err) {
    console.error('getSSOConfig failed', err)
    dispatch(setCloudSSO(null))
  }
}

export const createOrUpdateDesktopSSOConfig =
  ({ desktop_sso_entries, previous_entries, organization_id }) =>
  async (dispatch) => {
    try {
      const existingIds = new Set(previous_entries.map((entry) => entry.id).filter(Boolean))
      const currentIds = new Set(desktop_sso_entries.map((entry) => entry.id).filter(Boolean))

      const deletedIds = [...existingIds].filter((id) => !currentIds.has(id))

      for (const id of deletedIds) {
        try {
          await fetchingAPI(`${apiService.auth}/sso/config/${id}`, 'DELETE', dispatch)
        } catch (err) {
          console.warn(`Failed to delete desktop SSO config with id=${id}:`, err)
        }
      }

      const updatedConfigs = []
      for (const entry of desktop_sso_entries) {
        const method = entry.id ? 'PATCH' : 'POST'
        const url = entry.id
          ? `${apiService.auth}/sso/config/${entry.id}`
          : `${apiService.auth}/sso/config`

        const body = {
          ...entry,
          organization_id,
        }

        const savedConfig = await fetchingAPI(url, method, dispatch, JSON.stringify(body))
        updatedConfigs.push(savedConfig)
      }

      if (updatedConfigs.length === 0) {
        dispatch(setDesktopSSO({ desktop_sso_entries: [] }))
        if (deletedIds.length > 0) {
          toast.success('All desktop SSO entries deleted')
        }
      } else {
        dispatch(setDesktopSSO({ desktop_sso_entries: updatedConfigs }))
        toast.success('Desktop SSO config updated successfully')
      }

      dispatch(closeModal())
    } catch (err) {
      toast.error('Failed to update desktop SSO config', { cause: err })
      console.error('Failed to update desktop SSO config entries:', err)
      dispatch(closeModal())
    }
  }

export const fetchDesktopSSOConfig = (orgId) => async (dispatch) => {
  try {
    const configs = await fetchingAPI(`${apiService.auth}/sso/config/org/${orgId}`, 'GET', dispatch)

    if (!configs || configs.length === 0) {
      dispatch(setDesktopSSO(null))
      return
    }

    dispatch(
      setDesktopSSO({
        desktop_sso_entries: configs,
      })
    )
  } catch (err) {
    console.error('getDesktopSSOConfig failed', err)
    dispatch(setDesktopSSO(null))
  }
}

// Provisions a brand-new org's desktop SSO end to end in Keycloak (realm, client,
// identity provider, auth-flow wiring) and creates the config rows. Only usable
// when the org has no existing desktop SSO configs.
export const provisionDesktopSSO =
  ({ organization_id, ...payload }) =>
  async (dispatch) => {
    try {
      const result = await fetchingAPI(
        `${apiService.auth}/sso/provision`,
        'POST',
        dispatch,
        JSON.stringify({ organization_id, ...payload })
      )

      // The broker redirect URI must be registered on the customer's IdP, so keep
      // the toast until dismissed to give the operator time to copy it.
      toast.success('Desktop SSO provisioned successfully', {
        description: `Redirect URI to register on the customer IdP: ${result?.idp_redirect_uri}`,
        duration: Infinity,
      })

      await dispatch(fetchDesktopSSOConfig(organization_id))
      dispatch(closeModal())
    } catch (err) {
      console.error('provisionDesktopSSO failed', err)
      const message =
        err?.detail || err?.message || 'Failed to provision desktop SSO. Check the logs.'
      // 500s are already surfaced by the response handler.
      if (err?.status !== 500) {
        toast.error(message, { cause: err })
      }
    }
  }
